import { ICoupon } from '@/lib/schemas/Coupon.zod';
import { ICustomer } from '@/lib/schemas/Customer.zod';
import { IPayMethod } from '@/lib/schemas/PayMethod.zod';
import type { IShippingLocation } from '@/services';
import type { IShippingMethod } from '@/types/checkout';

import { isDebug, isDevelopment, isStaging } from '../env.service';
import { ApiResponse } from '../http/BaseHttpClient';
import AbstractService from './AbstractService';

export const CARD_GATEWAY_PROVIDERS = ['PRISMA', 'PAYWAY', 'FISERV', 'GETNET', 'PAGO_360'] as const;
export type CardGatewayProvider = (typeof CARD_GATEWAY_PROVIDERS)[number];

export function isCardGateway(paymentType?: string | null): boolean {
  return CARD_GATEWAY_PROVIDERS.includes(paymentType as CardGatewayProvider);
}

export interface ILocalCheckoutItem {
  id?: string;
  quantity: number;
  pricetype: 'public' | 'wholesaler' | 'reseller' | string;
  product: {
    id?: string;
    name?: string;
    price_public_value?: number | null;
    price_wholesale_value?: number | null;
    price_retail_value?: number | null;
    picture?: { url?: string } | null;
    [key: string]: unknown;
  };
  variant?: { id?: string } | null;
  variants?: Array<{ id?: string }>; // legacy
}

export interface ILocalCheckoutData {
  paymentType?: string | null;
  paymentMethod?: IPayMethod | null;
  customerInfo?: ICustomer | Record<string, any> | null;
  shipping: {
    method?: IShippingMethod | null;
    location?: IShippingLocation | null;
    locationId?: string | number;
    address?: Record<string, any> | null;
    addressId?: string | number;
  };
  coupon?: ICoupon | null;
}

export class PaymentService extends AbstractService {
  async getPaymentMethods(params?: Record<string, any>): Promise<IPayMethod[]> {
    const response = await this.get<IPayMethod[]>(this.endpoint('shopPayments'), params);
    const data = (response as any)?.data ?? response;

    this.logger.debug({ message: 'PaymentService:getPaymentMethods', count: (data as any)?.length, params });

    if ((data as any)?.error) {
      throw new Error((data as any).error?.message || 'Failed to fetch payment methods');
    }

    return data as IPayMethod[];
  }

  async getPayMethods(type?: string, status?: string): Promise<IPayMethod[]> {
    const params: Record<string, string> = {};
    if (type) params.type = type;
    if (status) params.status = status;

    const methods = await this.getPaymentMethods(params);
    return methods ?? [];
  }

  resolveMPInitPoint(preference?: { id?: string; init_point?: string; sandbox_init_point?: string } | null): string {
    if (!preference) {
      throw new Error('No payment preference provided');
    }
    if (!preference.init_point || !preference.sandbox_init_point) {
      this.logger.error({ message: 'Invalid payment preference object', preference });
      throw new Error('Invalid payment preference: missing init points');
    }
    if (isDevelopment() || isStaging()) {
      this.logger.debug({ message: 'Resolving MercadoPago init point in debug mode, using sandbox URL', preference });
      return preference.sandbox_init_point;
    }

    return preference.init_point;
  }
}

export const paymentService = PaymentService.getInstance();

export default PaymentService;
